import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useElementSize<T extends HTMLElement = HTMLDivElement>() {
  const [size, setSize] = useState<[number, number]>([0, 0]);
  const elementRef = useRef<T>(null);

  const updateSize = useCallback(() => {
    const element = elementRef.current;
    if (element) {
      setSize([element.offsetWidth, element.offsetHeight]);
    }
  }, []);

  useEffect(() => {
    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);

    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSize]);

  return [elementRef, ...size] as const;
}

/**
 * Watches an element's width and reports whether it is narrower than `threshold`.
 * For controls that carry labels while they fit and abbreviate to icons when they
 * don't.
 *
 * Measures the *element*, not the viewport, because the two differ: the nav rail's
 * expanded/collapsed state changes how much room a page actually gets, so the same
 * viewport width can be roomy or cramped. Same reasoning as the container query at
 * `WorkspacePage.module.scss`.
 *
 * Deliberately stores the boolean rather than the width, unlike {@link useElementSize}:
 * setting an unchanged boolean lets React bail out, so a consumer re-renders only when
 * the threshold is actually crossed. Width state re-renders on every observed pixel,
 * which is wasted work when the only question is "does the labelled form still fit?"
 * — and costly when the observing component does real work per render.
 *
 * `null` until the element has been measured, so a consumer that mounts something
 * costly in one form (a media element that starts loading) can wait for the answer
 * instead of committing the wrong form for a frame. Consumers that only pick a
 * layout treat `null` as whichever form is cheaper to leave.
 *
 * `useLayoutEffect`, not `useEffect`: the first measurement lands before the browser
 * paints, so the unmeasured form is never shown.
 */
export function useNarrowerThan<T extends HTMLElement = HTMLDivElement>(threshold: number) {
  const [isNarrower, setIsNarrower] = useState<boolean | null>(null);
  const elementRef = useRef<T>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const measure = () => {
      const width = element.offsetWidth;
      // 0 means "not laid out yet", which is not an answer.
      setIsNarrower(width > 0 ? width < threshold : null);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [threshold]);

  return [elementRef, isNarrower] as const;
}

/**
 * The staged form of {@link useNarrowerThan}: watches one element's width against an ordered list of
 * thresholds and reports how many of them it has fallen below. Pass them widest-first and get back 0
 * while the element is wider than all of them, 1 below the first, 2 below the second, and so on.
 *
 * Exists because two `useNarrowerThan` calls would need two refs, and therefore two elements. A row
 * that degrades in stages has to key EVERY stage off the same measurement, or the stages interact:
 * if a later stage hands width back to something an earlier stage measures, shrinking the container
 * can UN-degrade it, and the row gets more elaborate as it gets narrower.
 *
 * That is not hypothetical — it shipped here. The library header's verb-label threshold measured
 * `.headerLeftTitle`, whose width grows by ~309px when the right-hand cluster abbreviates, so
 * crossing the abbreviation threshold downward pushed the title row back OVER its own threshold and
 * the labels reappeared on a narrower window. Both consumers now measure the row itself, whose width
 * nothing inside it can change: `LibraryContent` and `LibrariesNav`.
 *
 * Stores the step, not the width, for the same reason `useNarrowerThan` stores a boolean: an
 * unchanged integer lets React bail out, so a consumer re-renders only when a stage is actually
 * crossed rather than on every observed pixel.
 *
 * `thresholds` may be rebuilt every render — a stage's cost often depends on what is currently
 * rendered — because the effect keys off the values rather than the array's identity.
 *
 * CALLERS OWN MONOTONICITY. Each stage must demand strictly less width than the one above it. This
 * hook guarantees the stages are decided from one width; it cannot check that a lower stage is
 * genuinely cheaper than a higher one.
 *
 * An unmeasured element counts as roomy (step 0): a consumer renders its roomy form rather than
 * flickering out of its narrow one. (`useNarrowerThan` answers `null` instead, for consumers that
 * must not commit either form before the measurement.) The first measurement lands in
 * `useLayoutEffect`, before paint, so the default is never shown on a narrow element.
 */
export function useWidthTier<T extends HTMLElement = HTMLDivElement>(thresholds: number[]) {
  const [step, setStep] = useState(0);
  const elementRef = useRef<T>(null);

  // Read through a ref so the effect can depend on the VALUES (`key`) instead of the array's
  // identity, which a caller computing thresholds inline would change every render.
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;
  const key = thresholds.join(',');

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const measure = () => {
      const width = element.offsetWidth;
      // 0 means "not laid out yet", which is not the same as narrow — hence the roomy default.
      setStep(
        width > 0 ? thresholdsRef.current.filter((threshold) => width < threshold).length : 0,
      );
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [key]);

  return [elementRef, step] as const;
}
